"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFromLocalCache = exports.fromLocalCache = exports.useLocalCache = exports.LocalCacheEvent = void 0;
const lodash_1 = require("lodash");
const uuid_1 = require("uuid");
const react_1 = require("react");
const event_1 = require("../lib/event");
var LocalCacheEvent;
(function (LocalCacheEvent) {
    LocalCacheEvent["Set"] = "local-cache:set";
    LocalCacheEvent["Reset"] = "local-cache:reset";
})(LocalCacheEvent = exports.LocalCacheEvent || (exports.LocalCacheEvent = {}));
/**
 * Get and set state hook through `localStorage`.
 * @param key The localStorage key.
 * @param initial The default value if localStorage item is not available.
 * @returns The local cache.
 */
function useLocalCache(key, initial) {
    // save to refs to avoid re-rendering
    const getInitial = typeof initial === 'function' ? initial : () => initial;
    const initialRef = (0, react_1.useRef)(getInitial);
    initialRef.current = getInitial;
    const keyRef = (0, react_1.useRef)(key);
    keyRef.current = key;
    // use id to identify event caller
    const idRef = (0, react_1.useRef)('');
    idRef.current = idRef.current || (0, uuid_1.v4)();
    // get value (string or null) from localStorage and parse value
    const get = (0, react_1.useCallback)(() => {
        const value = localStorage.getItem(keyRef.current);
        if (value === null) {
            return initialRef.current();
        }
        let parsed = null;
        try {
            parsed = JSON.parse(value);
        }
        catch (_a) { }
        return parsed === null ? initialRef.current() : parsed;
    }, []);
    const [value, setValue] = (0, react_1.useState)(() => get());
    const valueRef = (0, react_1.useRef)(value);
    const changed = (0, react_1.useMemo)(() => {
        // check via stringify, lodash isEqual sometimes not checking properly
        const initialValue = initialRef.current();
        // skip check if there is no initial value
        if (typeof initialValue === 'undefined' || initialValue === null) {
            return false;
        }
        let equal = false;
        try {
            equal = JSON.stringify(value) === JSON.stringify(initialValue);
        }
        catch (_a) { }
        equal = equal || (0, lodash_1.isEqual)(value, initialValue);
        return !equal;
    }, [value]);
    const set = (0, react_1.useCallback)(value => {
        const data = typeof value === 'function'
            ? value(valueRef.current)
            : value;
        const key = keyRef.current;
        localStorage.setItem(key, JSON.stringify(data));
        event_1.CoreEvent.dispatch(LocalCacheEvent.Set, {
            key,
            data,
            id: idRef.current
        });
        valueRef.current = data;
        setValue(data);
    }, []);
    const refresh = (0, react_1.useCallback)(() => {
        const value = get();
        valueRef.current = value;
        setValue(value);
    }, [get]);
    const reset = (0, react_1.useCallback)(() => {
        const key = keyRef.current;
        localStorage.removeItem(key);
        event_1.CoreEvent.dispatch(LocalCacheEvent.Reset, {
            key,
            id: idRef.current
        });
        refresh();
    }, [refresh]);
    (0, react_1.useEffect)(() => {
        return event_1.CoreEvent.handle([LocalCacheEvent.Set, LocalCacheEvent.Reset], data => {
            // refresh value when set and reset events are triggered
            // from a different hook to avoid multiple setValue() calls
            if (data && data.id !== idRef.current && data.key === keyRef.current) {
                refresh();
            }
        });
    }, [refresh]);
    const cache = (0, react_1.useMemo)(() => {
        return { value, changed, set, reset, refresh };
    }, [value, changed, set, reset, refresh]);
    return cache;
}
exports.useLocalCache = useLocalCache;
// non-hook implementation
function fromLocalCache(options = {}) {
    const id = options.id || (0, uuid_1.v4)();
    const cache = {};
    cache.get = key => {
        const value = localStorage.getItem(key);
        try {
            return value ? JSON.parse(value) : null;
        }
        catch (_a) {
            return null;
        }
    };
    cache.set = (key, data) => {
        localStorage.setItem(key, JSON.stringify(data));
        event_1.CoreEvent.dispatch(LocalCacheEvent.Set, {
            id,
            key,
            data
        });
        return cache;
    };
    cache.reset = key => {
        localStorage.removeItem(key);
        event_1.CoreEvent.dispatch(LocalCacheEvent.Reset, {
            id,
            key
        });
        return cache;
    };
    cache.compare = (a, b) => {
        // check for both undefined and null
        // NOTE: taken from https://stackoverflow.com/a/2647888/7013346
        let equal = a == null || b == null;
        try {
            // check via stringify, lodash isEqual sometimes not checking properly
            equal = equal || JSON.stringify(a) === JSON.stringify(b);
        }
        catch (_a) { }
        return equal || (0, lodash_1.isEqual)(a, b);
    };
    cache.didChange = (key, initialValue) => {
        return !cache.compare(cache.get(key), initialValue);
    };
    return cache;
}
exports.fromLocalCache = fromLocalCache;
function useFromLocalCache() {
    // use id to identify event caller
    const idRef = (0, react_1.useRef)('');
    idRef.current = idRef.current || (0, uuid_1.v4)();
    const [localCache, setLocalCache] = (0, react_1.useState)(() => {
        return fromLocalCache({ id: idRef.current });
    });
    // update state when set and reset events are triggered
    (0, react_1.useEffect)(() => {
        const cancelSet = event_1.CoreEvent.handle(LocalCacheEvent.Set, data => {
            // only update when events are triggered from a different hook
            if (data && data.id !== idRef.current) {
                setLocalCache(fromLocalCache({ id: idRef.current }));
            }
        });
        const cancelReset = event_1.CoreEvent.handle(LocalCacheEvent.Reset, () => setLocalCache(fromLocalCache({ id: idRef.current })));
        return () => {
            cancelSet();
            cancelReset();
        };
    }, []);
    return localCache;
}
exports.useFromLocalCache = useFromLocalCache;
//# sourceMappingURL=localCache.hook.js.map